import { useMemo } from 'react'

import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { type SegmentedControlOption } from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { FieldLabel, FieldRoot } from '~/components/common/Field'
import { RequiredMark } from '~/components/common/RequiredMark'
import {
  JEPX_VOLUME_CONSTRAINT,
  constrainNumberInput,
  violatesJepxStep,
} from '~/models/numberInputConstraint'
import type { NumberPreset } from '~/pages/SpotBidding/components/PresetNumberField'
import { PresetNumberField } from '~/pages/SpotBidding/components/PresetNumberField'
import type { BulkVolumeMode } from '~/pages/SpotBidding/models/bulkCreate'

/** 目標カバー率のプリセット (%)。 design `spot.html` `#bblk-pct-preset-group`。 */
const COVERAGE_PRESETS: readonly NumberPreset[] = [50, 70, 80, 90, 100].map(
  (p) => ({ value: String(p), label: `${p}%` }),
)

/** 数量指定モードのプリセット (MW)。 AC-BSP-005c / design `spot.html` `.bblk-fixedmw-preset`。 */
const FIXED_VOLUME_PRESETS: readonly NumberPreset[] = [1, 5, 10, 20, 50].map(
  (mw) => ({ value: String(mw), label: `${mw} MW` }),
)

/** 目標カバー率 (%) の入力範囲 (AC-BSP-005: 1〜200 の整数。100% 超を許容する)。 */
const COVERAGE_MIN_PERCENT = 1
const COVERAGE_MAX_PERCENT = 200

/** 数量指定モードの初期値 (MW)。 design `spot.html` は 10 MW を active にしている。 */
export const FIXED_VOLUME_DEFAULT = '10'

/** 数量の決め方と、 その決め方で使う数量入力の生値 (文字列 state のまま)。 */
export type BulkVolumeInput = {
  volumeMode: BulkVolumeMode
  /** 目標カバー率 (%)。 `volumeMode === 'coverage'` のときだけ使う。 */
  coverage: string
  /** 1 コマあたり数量 (MW)。 `volumeMode === 'fixed'` のときだけ使う。 */
  fixedVolume: string
}

/**
 * 数量入力の不備を、 利用者に見せる文言として返す (不備が無ければ undefined)。
 *
 * 値域は決め方ごとに違う (目標カバー率 = 1〜200%、 数量指定 = JEPX 数量値域
 * 0.1〜9999.9 MW / 0.1 MW 単位、 AC-BSP-005c) ので、 判定と文言をここに 1 つだけ
 * 置き、 呼び出し側 ([[BulkCreateModal]]) は「不備があるか / 何と出すか」だけを
 * 受け取る (= 値域を両側に複製しない)。
 */
export const bulkVolumeInputError = (
  input: BulkVolumeInput,
  t: TFunction,
): string | undefined => {
  if (input.volumeMode === 'fixed') {
    const mw = Number(input.fixedVolume)
    const valid =
      Number.isFinite(mw) &&
      mw >= JEPX_VOLUME_CONSTRAINT.min &&
      mw <= JEPX_VOLUME_CONSTRAINT.max &&
      !violatesJepxStep(mw, JEPX_VOLUME_CONSTRAINT.step)
    return valid
      ? undefined
      : t('page:biddingSpot.bulkCreate.commitDisabledFixedVolume', {
          min: JEPX_VOLUME_CONSTRAINT.min,
          max: JEPX_VOLUME_CONSTRAINT.max,
        })
  }
  const pct = Number(input.coverage)
  const valid =
    Number.isFinite(pct) &&
    pct >= COVERAGE_MIN_PERCENT &&
    pct <= COVERAGE_MAX_PERCENT
  return valid
    ? undefined
    : t('page:biddingSpot.bulkCreate.commitDisabledCoverage', {
        min: COVERAGE_MIN_PERCENT,
        max: COVERAGE_MAX_PERCENT,
      })
}

type Props = BulkVolumeInput & {
  /**
   * 決め方の切替。 数量指定は単一エリア限定 (AC-BSP-005c) で対象エリアの選択を
   * 作り直す必要があるが、 エリアは本 component の外の関心なので呼び出し側へ返す。
   */
  onVolumeModeChange: (next: BulkVolumeMode) => void
  onCoverageChange: (next: string) => void
  /** 数量 (MW) の変更。 JEPX 数量値域への丸めは本 component 内で済ませて渡す。 */
  onFixedVolumeChange: (next: string) => void
}

/**
 * 一括作成モーダルの「数量の決め方」欄 (design `spot.html` `#bblk-inputmode` +
 * `#bblk-pct-preset-group` / `#bblk-mode-fixed`)。
 *
 * 決め方の 2 択 (目標カバー率 AC-BSP-005a / 数量指定 AC-BSP-005c) と、 決め方ごとに
 * 差し替わる数量入力欄を 1 単位として持つ。 プリセット値・値域・確定不可の文言は
 * この決め方に固有なので、 [[bulkVolumeInputError]] と併せてここに閉じる。
 */
export const BulkVolumeModeField = ({
  volumeMode,
  coverage,
  fixedVolume,
  onVolumeModeChange,
  onCoverageChange,
  onFixedVolumeChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  /**
   * 数量の決め方の 2 択 (design `spot.html` `#bblk-inputmode`)。
   * 業務ロジックの正本は mock-app `JepxSpotBid.razor` の `InputMode` ("gap"/"fixed")。
   */
  const volumeModeSegments = useMemo<
    Array<SegmentedControlOption<BulkVolumeMode>>
  >(
    () => [
      {
        label: t('page:biddingSpot.bulkCreate.volumeModeCoverage'),
        value: 'coverage',
      },
      {
        label: t('page:biddingSpot.bulkCreate.volumeModeFixed'),
        value: 'fixed',
      },
    ],
    [t],
  )

  return (
    <>
      {/* 数量の決め方 (design `spot.html` `#bblk-inputmode` = `.ecl-segmented`)。
          DS `SegmentedControl` の a11y 補正ラッパを使う (= design と同じ見た目、
          radio の role/キーボード操作つき)。 */}
      <FieldRoot>
        <FieldLabel>
          {t('page:biddingSpot.bulkCreate.volumeModeLabel')}
          <RequiredMark />
        </FieldLabel>
        <AccessibleSegmentedControl
          aria-label={t('page:biddingSpot.bulkCreate.volumeModeLabel')}
          name="bulkCreate-volumeMode"
          onSelect={onVolumeModeChange}
          options={volumeModeSegments}
          selected={volumeMode}
        />
      </FieldRoot>

      {/* 数量は決め方ごとに入力欄を差し替える。 「プリセット + 直接入力」 の
          レイアウトは design (`#bblk-pct-preset-group` / `#bblk-mode-fixed`) で
          共通なので [[PresetNumberField]] に持たせ、 ここには値域と setter の
          差分だけを置く。 */}
      {volumeMode === 'fixed' ? (
        /* 数量指定 (AC-BSP-005c)。 0.1〜9999.9 MW / 0.1 MW 単位。 */
        <PresetNumberField
          inputId="bulk-fixed-volume"
          inputWidth="110px"
          label={t('page:biddingSpot.bulkCreate.fixedVolumeLabel')}
          max={JEPX_VOLUME_CONSTRAINT.max}
          min={JEPX_VOLUME_CONSTRAINT.min}
          onChange={(next) =>
            onFixedVolumeChange(
              constrainNumberInput(next, JEPX_VOLUME_CONSTRAINT),
            )
          }
          presets={FIXED_VOLUME_PRESETS}
          step={JEPX_VOLUME_CONSTRAINT.step}
          value={fixedVolume}
        />
      ) : (
        /* 目標カバー率 (AC-BSP-005a)。 直接入力には「数量の決め方」のセグメント名
           と同名にならないよう、 可視ラベルと同じ aria-label を付ける
           (= id/htmlFor で紐付けると label 文言がセグメント名と衝突する)。 */
        <PresetNumberField
          inputAriaLabel={t(
            'page:biddingSpot.bulkCreate.coverageInputAriaLabel',
          )}
          inputWidth="90px"
          label={t('page:biddingSpot.bulkCreate.coverageLabel')}
          max={COVERAGE_MAX_PERCENT}
          min={COVERAGE_MIN_PERCENT}
          onChange={onCoverageChange}
          presets={COVERAGE_PRESETS}
          step={1}
          value={coverage}
        />
      )}
    </>
  )
}
